import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, Clock, AlertTriangle, Users, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ReadinessValidationChecklist } from './ReadinessValidationChecklist';

interface TeamReadinessConfirmationProps {
  gameId: string;
  teamId?: number;
  teamSide: 'home' | 'away';
  selectedPlayers: string[];
  startingLineup: string[];
  isReady: boolean;
  readinessDeadline?: string;
  gameTableType?: 'games' | 'venue_games' | null;
  canConfirmReadiness: boolean;
  onReadinessChange: (isReady: boolean) => void;
}

export const TeamReadinessConfirmation: React.FC<TeamReadinessConfirmationProps> = ({
  gameId,
  teamId,
  teamSide,
  selectedPlayers,
  startingLineup,
  isReady,
  readinessDeadline,
  gameTableType,
  canConfirmReadiness,
  onReadinessChange
}) => {
  const [confirming, setConfirming] = useState(false);
  const [notes, setNotes] = useState('');
  const { toast } = useToast();

  const handleConfirmReadiness = async () => {
    console.log('🎯 CONFIRM TEAM READY BUTTON CLICKED!');
    console.log('🔍 Initial state:', { canConfirmReadiness, confirming, isReady });
    
    if (!canConfirmReadiness) {
      console.error('❌ Cannot confirm readiness - permission denied');
      toast({
        title: "Permission Denied",
        description: "You don't have permission to confirm team readiness",
        variant: "destructive"
      });
      return;
    }
    
    if (confirming) {
      console.warn('⚠️ Already confirming, skipping');
      return;
    }

    console.log('🔍 Confirm Readiness - selectedPlayers:', selectedPlayers);
    console.log('🔍 Confirm Readiness - startingLineup:', startingLineup);
    console.log('🔍 Confirm Readiness - counts:', { 
      selectedPlayersCount: selectedPlayers.length, 
      startingLineupCount: startingLineup.length 
    });

    // Validate requirements
    if (selectedPlayers.length === 0) {
      console.error('❌ Validation failed: No players selected');
      toast({
        title: "Cannot Confirm Readiness",
        description: `Please select at least one player for the game roster. Currently have ${selectedPlayers.length} players selected.`,
        variant: "destructive"
      });
      return;
    }

    if (startingLineup.length === 0) {
      console.error('❌ Validation failed: No starting lineup');
      toast({
        title: "Cannot Confirm Readiness",
        description: `Please designate at least one starting player. Currently have ${startingLineup.length} starters selected.`,
        variant: "destructive"
      });
      return;
    }
    
    console.log('✅ Validation passed, proceeding with confirmation');

    setConfirming(true);
    try {
      console.log('Confirming team readiness:', { gameId, teamSide, gameTableType, selectedPlayers, startingLineup });
      
      const updateField = teamSide === 'home' ? 'home_team_ready' : 'away_team_ready';
      const playersField = teamSide === 'home' ? 'home_ready_players' : 'away_ready_players';
      const lineupField = teamSide === 'home' ? 'home_starting_lineup' : 'away_starting_lineup';
      
      const updateData = {
        [updateField]: true,
        [playersField]: selectedPlayers,
        [lineupField]: startingLineup,
        [`${teamSide}_team_confirmed_at`]: new Date().toISOString(),
        [`${teamSide}_team_notes`]: notes || null
      };

      // Update the correct table based on gameTableType
      if (gameTableType === 'venue_games') {
        const { error } = await supabase
          .from('venue_games')
          .update(updateData)
          .eq('id', gameId);

        if (error) {
          console.error('❌ Failed to update venue_games:', error);
          throw new Error(`Database error (venue_games.${Object.keys(updateData).join(', ')}): ${error.message}`);
        }
      } else if (gameTableType === 'games') {
        const { error } = await supabase
          .from('games')
          .update(updateData)
          .eq('id', gameId);

        if (error) {
          console.error('❌ Failed to update games:', error);
          throw new Error(`Database error (games.${Object.keys(updateData).join(', ')}): ${error.message}`);
        }
      } else {
        // Fallback: try both tables
        console.log('⚠️ Game table type unknown, trying both tables');
        
        const [venueResult, gameResult] = await Promise.allSettled([
          supabase.from('venue_games').update(updateData).eq('id', gameId),
          supabase.from('games').update(updateData).eq('id', gameId)
        ]);

        const venueError = venueResult.status === 'rejected' ? venueResult.reason : venueResult.value?.error;
        const gameError = gameResult.status === 'rejected' ? gameResult.reason : gameResult.value?.error;

        if (venueError && gameError) {
          console.error('❌ Both updates failed:', { venueError, gameError });
          throw new Error(`Failed to update game in both tables. Please contact support.`);
        }
      }

      onReadinessChange(true);
      
      toast({
        title: "Team Readiness Confirmed",
        description: `${teamSide === 'home' ? 'Home' : 'Away'} team is ready to start the game`,
      });
    } catch (error: any) {
      console.error('❌ Error confirming team readiness:', error);
      
      const errorMessage = error?.message || 'Failed to confirm team readiness';
      const isNetworkError = error?.message?.includes('fetch') || error?.message?.includes('network');
      
      toast({
        title: isNetworkError ? "Network Error" : "Database Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleRevokeReadiness = async () => {
    if (confirming) return;

    setConfirming(true);
    try {
      const updateField = teamSide === 'home' ? 'home_team_ready' : 'away_team_ready';
      
      // Update the correct table based on gameTableType
      if (gameTableType === 'venue_games') {
        const { error } = await supabase
          .from('venue_games')
          .update({
            [updateField]: false,
            [`${teamSide}_team_confirmed_at`]: null,
            [`${teamSide}_team_notes`]: null
          })
          .eq('id', gameId);

        if (error) throw error;
      } else if (gameTableType === 'games') {
        const { error } = await supabase
          .from('games')
          .update({
            [updateField]: false,
            [`${teamSide}_team_confirmed_at`]: null,
            [`${teamSide}_team_notes`]: null
          })
          .eq('id', gameId);

        if (error) throw error;
      } else {
        // Fallback: try both tables
        const venueUpdate = supabase
          .from('venue_games')
          .update({
            [updateField]: false,
            [`${teamSide}_team_confirmed_at`]: null,
            [`${teamSide}_team_notes`]: null
          })
          .eq('id', gameId);

        const gameUpdate = supabase
          .from('games')
          .update({
            [updateField]: false,
            [`${teamSide}_team_confirmed_at`]: null,
            [`${teamSide}_team_notes`]: null
          })
          .eq('id', gameId);

        // Try both updates, at least one should succeed
        await Promise.allSettled([venueUpdate, gameUpdate]);
      }

      onReadinessChange(false);
      setNotes('');
      
      toast({
        title: "Team Readiness Revoked",
        description: `${teamSide === 'home' ? 'Home' : 'Away'} team readiness has been revoked`,
      });
    } catch (error) {
      console.error('Error revoking team readiness:', error);
      toast({
        title: "Error",
        description: "Failed to revoke team readiness",
        variant: "destructive"
      });
    } finally {
      setConfirming(false);
    }
  };

  const getReadinessIcon = () => {
    if (isReady) return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (readinessDeadline && new Date() > new Date(readinessDeadline)) {
      return <AlertTriangle className="h-5 w-5 text-red-600" />;
    }
    return <Clock className="h-5 w-5 text-yellow-600" />;
  };

  const getReadinessStatus = () => {
    if (isReady) return "Ready";
    if (readinessDeadline && new Date() > new Date(readinessDeadline)) {
      return "Deadline Passed";
    }
    return "Pending";
  };

  const getReadinessVariant = () => {
    if (isReady) return "default";
    if (readinessDeadline && new Date() > new Date(readinessDeadline)) {
      return "destructive";
    }
    return "secondary";
  };

  const canConfirm = canConfirmReadiness && !isReady && selectedPlayers.length > 0 && startingLineup.length > 0;
  const deadlinePassed = readinessDeadline && new Date() > new Date(readinessDeadline);

  // Debug logging for button state
  console.log('🔍 TeamReadinessConfirmation render state:', {
    canConfirmReadiness,
    isReady,
    selectedPlayersLength: selectedPlayers.length,
    startingLineupLength: startingLineup.length,
    canConfirm,
    confirming,
    buttonDisabled: !canConfirm || confirming
  });

  return (
    <Card className={isReady ? "border-green-200 bg-green-50/50" : deadlinePassed ? "border-red-200 bg-red-50/50" : ""}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Readiness Confirmation
          </div>
          <Badge variant={getReadinessVariant()}>
            {getReadinessIcon()}
            {getReadinessStatus()}
          </Badge>
        </CardTitle>
        <CardDescription>
          Confirm that your team is ready to start the game
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Validation Checklist */}
        <ReadinessValidationChecklist
          selectedPlayersCount={selectedPlayers.length}
          startingLineupCount={startingLineup.length}
          minimumPlayers={1}
          minimumStarters={1}
        />

        {/* Deadline Information */}
        {readinessDeadline && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Readiness Deadline</h4>
            <p className="text-sm text-muted-foreground">
              {new Date(readinessDeadline).toLocaleString()}
            </p>
            {deadlinePassed && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">Deadline has passed</span>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {!isReady && canConfirmReadiness && (
          <div className="space-y-2">
            <Label htmlFor="readiness-notes">Additional Notes (Optional)</Label>
            <Textarea
              id="readiness-notes"
              placeholder="Any additional information about team readiness..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={confirming}
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {!isReady ? (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Confirm Team Ready button clicked');
                handleConfirmReadiness();
              }}
              disabled={!canConfirm || confirming}
              className="flex-1"
            >
              {confirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Team Ready
                </>
              )}
            </Button>
          ) : (
            canConfirmReadiness && (
              <Button
                onClick={handleRevokeReadiness}
                disabled={confirming}
                variant="outline"
                className="flex-1"
              >
                {confirming ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Revoking...
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4 mr-2" />
                    Revoke Readiness
                  </>
                )}
              </Button>
            )
          )}
        </div>

        {/* Status Information */}
        {isReady && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Team is ready to start the game!</span>
            </div>
            <p className="text-green-700 text-sm mt-1">
              Waiting for the other team and final game approval.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};